import { isoToBrDate } from '@/features/Vacations/utils/vacationDates';
import { computeReturnDate } from '@/features/Vacations/utils/vacationRules';
import { getModalityLabel } from '@/features/Vacations/vacations.definitions';
import { VacationModality } from '@/features/Vacations/models/vacation.types';
import type { ApprovalData } from '../clientApprovalPage.types';
import type { useClientApproval } from '../hooks/useClientApproval';
import {
    REJECTION_NOTE_LABEL,
    REJECTION_NOTE_MAX_LENGTH,
    REJECTION_NOTE_PLACEHOLDER,
    SUBMITTING_LABEL,
    SUBMIT_LABEL,
} from '../clientApprovalPage.definitions';
import { sortSegments } from '../clientApprovalPage.utils';
import { ClientPeriodChoices } from './ClientPeriodChoices';

interface ClientApprovalFormProps {
    data: ApprovalData;
    controller: ReturnType<typeof useClientApproval>;
}

export function ClientApprovalForm({ data, controller }: ClientApprovalFormProps) {
    const { openSegments, lockedBy, responses, setResponse, rejectedCount, note, setNote, canSubmit, choiceTitle, isSubmitting, submit } = controller;
    const segments = sortSegments(data.segments ?? []);
    const lockedOrders = data.lockedOrders ?? [];
    const lastSegment = segments[segments.length - 1];
    const returnDate = lastSegment ? computeReturnDate(lastSegment.startDate, lastSegment.days) : null;
    const periods = segments
        .map((segment) => `${isoToBrDate(segment.startDate)} a ${isoToBrDate(segment.endDate)} (${segment.days} dias)`)
        .join(' + ');

    return (
        <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-blue-900 mb-4">Confirmação de Férias</h2>
                <div className="space-y-3 mb-6">
                    <div>
                        <label className="block text-sm text-gray-600">Colaborador</label>
                        <p className="font-semibold text-gray-900">{data.collaboratorName}</p>
                    </div>
                    <div>
                        <label className="block text-sm text-gray-600">Modalidade</label>
                        <p className="font-semibold text-gray-900">{getModalityLabel(data.modality as VacationModality)}</p>
                    </div>
                    <div>
                        <label className="block text-sm text-gray-600">{segments.length > 1 ? 'Períodos' : 'Período'}</label>
                        <p className="font-semibold text-gray-900">{periods || '—'}</p>
                    </div>
                    <div>
                        <label className="block text-sm text-gray-600">Dias de gozo</label>
                        <p className="font-semibold text-gray-900">{data.totalDays} dias</p>
                    </div>
                    {returnDate && (
                        <div>
                            <label className="block text-sm text-gray-600">Retorno ao trabalho</label>
                            <p className="font-semibold text-gray-900">{isoToBrDate(returnDate)}</p>
                        </div>
                    )}
                </div>

                <div className="space-y-4">
                    <ClientPeriodChoices
                        title={choiceTitle}
                        segments={segments}
                        lockedOrders={lockedOrders}
                        lockedBy={lockedBy}
                        responses={responses}
                        onChange={setResponse}
                        disabled={isSubmitting}
                    />

                    {rejectedCount > 0 && (
                        <div className="bg-white p-4 rounded border border-gray-200">
                            <label htmlFor="client-rejection-note" className="block text-sm font-semibold text-gray-900 mb-2">
                                {REJECTION_NOTE_LABEL}
                            </label>
                            <textarea
                                id="client-rejection-note"
                                value={note}
                                maxLength={REJECTION_NOTE_MAX_LENGTH}
                                onChange={(event) => setNote(event.target.value)}
                                placeholder={REJECTION_NOTE_PLACEHOLDER}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                rows={3}
                            />
                        </div>
                    )}
                </div>

                {openSegments.length > 0 && (
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!canSubmit}
                        className="w-full mt-4 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? SUBMITTING_LABEL : SUBMIT_LABEL}
                    </button>
                )}
            </div>
        </div>
    );
}
